import { useState, useRef, useEffect } from 'react';
import { TimeInput } from '@mantine/dates';
import styles from './TimeDropdown.module.scss';

interface TimeDropdownProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
}

export const TimeDropdown = ({ value, onChange, error }: TimeDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<'top' | 'bottom'>('bottom');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hours = value?.split(':')[0] || '00';
  const minutes = value?.split(':')[1] || '00';

  const handleHourSelect = (hour: string) => {
    onChange(`${hour}:${minutes}`);
  };

  const handleMinuteSelect = (minute: string) => {
    onChange(`${hours}:${minute}`);
  };

  useEffect(() => {
    if (isOpen) {
      setPosition('top');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mouseup', handleClickOutside);
    return () => document.removeEventListener('mouseup', handleClickOutside);
  }, [dropdownRef]);

  return (
    <div className={styles.timeDropdownContainer} ref={containerRef}>
      <TimeInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsOpen(true)}
        onClick={() => setIsOpen(true)}
        styles={{
          input: {
            height: '48px',
            fontSize: '16px',
            border: error ? '1px solid red' : 'none',
          },
        }}
      />

      {isOpen && (
        <div
          ref={dropdownRef}
          className={`${styles.timeDropdown} ${
            position === 'top' ? styles.positionTop : styles.positionBottom
          }`}>
          <div className={styles.timeSelector}>
            <div className={styles.timeColumn}>
              <div className={styles.columnHeader}>Часы</div>
              <div className={styles.columnContent}>
                {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(
                  (hour) => (
                    <div
                      key={hour}
                      className={`${styles.timeOption} ${
                        hours === hour ? styles.selected : ''
                      }`}
                      onClick={() => handleHourSelect(hour)}>
                      {hour}
                    </div>
                  ),
                )}
              </div>
            </div>

            <div className={styles.timeColumn}>
              <div className={styles.columnHeader}>Мин.</div>
              <div className={styles.columnContent}>
                {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map(
                  (minute) => (
                    <div
                      key={minute}
                      className={`${styles.timeOption} ${
                        minutes === minute ? styles.selected : ''
                      }`}
                      onClick={() => handleMinuteSelect(minute)}>
                      {minute}
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
